import type { ReactNode } from 'react';

import { MenuItem, TextField } from '@mui/material';

import { FieldRow } from './FieldRow';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  label: ReactNode;
  description?: ReactNode;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Allow clearing to an empty value (renders a leading blank option). */
  allowEmpty?: boolean;
  emptyLabel?: string;
  /**
   * Explicit accessible name, for when `label` is rich content (e.g. a channel-scope chip/action
   * rendered alongside the text) rather than a plain string the control can read directly.
   */
  ariaLabel?: string;
}

/** Labelled single-select dropdown laid out as a settings row. */
export function SelectField({
  label,
  description,
  value,
  options,
  onChange,
  disabled = false,
  allowEmpty = false,
  emptyLabel = '—',
  ariaLabel,
}: SelectFieldProps) {
  return (
    <FieldRow label={label} description={description}>
      <TextField
        select
        size="small"
        fullWidth
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        slotProps={{
          htmlInput: { 'aria-label': ariaLabel ?? (typeof label === 'string' ? label : undefined) },
        }}
      >
        {allowEmpty ? (
          <MenuItem value="">
            <em>{emptyLabel}</em>
          </MenuItem>
        ) : null}
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </FieldRow>
  );
}
